
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import GlossMediaDisplay from '@/ui/GlossMediaDisplay.vue';
import { Subscription } from 'rxjs';
import { BeforeDestroyAware } from '@/util/lifecycle';
import FileRepository from '@/api/FileRepository';
import Image from '@/api/mjegb/Image';

interface DropInformation {
    type: string;
    id: string;
    category: string;
    name: string;
}

@Component({
    components: {
        GlossMediaDisplay,
    },
})
export default class StandardCell extends Vue implements BeforeDestroyAware {
    @Prop({ type: Number, required: true })
    public readonly pageIndex!: number;

    @Prop({ type: Number, required: true })
    public readonly row!: number;

    @Prop({ type: Number, required: true })
    public readonly column!: number;

    @Prop({ type: Boolean, required: false, default: false })
    public readonly largeBlock!: boolean;

    @Prop({ type: Object, required: false, default: null })
    public readonly value!: Image | null;

    public image: string | null = null;
    public text: string | null = null;
    public category: string | null = null;
    public editing = false;
    public uploading = false;

    public get insetImageClass(): any {
        const className = this.category === 'pictograms' ? 'mjegb-inset-image-full' : 'mjegb-inset-image-standard';
        return {
            [className]: true,
        };
    }

    @Inject()
    private fileRepository!: FileRepository;
    private uploadSubscription = new Subscription();

    public beforeDestroy(): void {
        this.uploadSubscription.unsubscribe();
    }

    public onHandleDrop(data: DropInformation | undefined, nativeEvent: DragEvent | undefined): void {
        if (data) {
            if (data.type === 'image') {
                this.category = data.category;
                this.image = data.id;
                this.text = this.category === 'pictograms' ? null : data.name;
            }
        } else if (nativeEvent) {
            const dataTransfer = nativeEvent.dataTransfer;
            if (dataTransfer && dataTransfer.files.length > 0) {
                this.onSelectFile(dataTransfer.files[0]);
            }
        }
    }

    public onDragEnterOrOver(transferData: any, nativeEvent: any): void {
        // This is necessary for the drag/drop polyfill for mobile browsers.
        // See: https://github.com/timruffles/mobile-drag-drop#polyfill-requires-dragenter-listener
        nativeEvent.preventDefault();
    }

    public onClear(): void {
        this.image = null;
        this.text = null;
        this.category = null;
    }

    public onInsertText(): void {
        this.category = 'text';
        this.text = '';
        this.editing = true;
    }

    public onClickUpload(): void {
        const fileInput: any = this.$refs.fileInput;
        if (fileInput) {
            fileInput.click();
        }
    }

    public onFileInputChanged(): void {
        const fileInput: any = this.$refs.fileInput;
        if (fileInput && fileInput.files.length > 0) {
            this.onSelectFile(fileInput.files[0]);
        }
    }

    public onSelectFile(file: File | null): void {
        if (file) {
            this.uploadSubscription.unsubscribe();
            this.onClear();
            this.uploadSubscription = this.fileRepository.uploadFile(file).subscribe((resource) => {
                this.uploading = resource.isLoading;
                if (resource.hasData()) {
                    this.category = 'upload';
                    this.image = '' + resource.data.id;
                    this.text = '';
                }
            });
        }
    }

    @Watch('value', { immediate: true })
    public onValueChanged(value: Image | null): void {
        if (value !== null) {
            if (value.id === '__text') {
                this.image = null;
            } else if (this.image !== value.id) {
                this.image = value.id;
            }
            if (this.category !== value.category) {
                this.category = value.category;
            }
            if (this.text !== value.label) {
                this.text = value.label;
            }
        } else {
            this.onClear();
        }
    }

    @Watch('image')
    public onImageChanged(image: string | null): void {
        const data: Image | null =
            this.category !== null
                ? {
                      id: image || '__text',
                      category: this.category,
                      label: this.text || '',
                      row: this.row,
                      column: this.column,
                  }
                : null;
        this.$emit('input', data);
    }

    @Watch('category')
    public onCategoryChanged(category: string | null): void {
        const data: Image | null =
            category !== null
                ? {
                      id: this.image || '__text',
                      category: category,
                      label: this.text || '',
                      row: this.row,
                      column: this.column,
                  }
                : null;
        this.$emit('input', data);
    }

    @Watch('text')
    public onTextChanged(text: string | null): void {
        const data: Image | null =
            this.category !== null
                ? {
                      id: this.image || '__text',
                      category: this.category,
                      label: text || '',
                      row: this.row,
                      column: this.column,
                  }
                : null;
        this.$emit('input', data);
    }
}
